<template>
  <form class="facility-form" @submit.prevent="onSubmit">
    <div class="facility-form__main">
      <h2 class="facility-form__header">
        {{ facility ? "*** UPDATE MODE ***" : "*** Add New Facility ***" }}
      </h2>
      <div class="form-control">
        <label class="form-label" for="company-dropdown">Company</label>
        <DropDownList
          v-if="companiesLoaded"
          id="company-dropdown"
          :data-items="pagedCompanies"
          v-model="selectedCompany"
          text-field="Name"
          data-item-key="Value"
          :virtual="companyVirtual"
          :filterable="true"
          @filterchange="onCompanyFilterChange"
          @change="onCompanyChanged"
          @pagechange="onCompanyPageChange"
          required
          :disabled="facility ? true : false"
        />
        <Loader v-else class="company-loader" size="large" />
      </div>
      <div class="form-control">
        <label class="form-label" for="industry-dropdown">Industries</label>

        <MultiSelect
          id="industry-dropdown"
          :items="sortedIndustries"
          v-model:value="selectedIndustries"
          :auto-close="false"
          placeholder="Select Industries"
          itemsName="Industries"
          text-field="category"
          data-item-key="id"
          required
        />
      </div>
      <div class="form-control">
        <label class="form-label" for="location-dropdown">Locations</label>

        <MultiSelect
          id="location-dropdown"
          :items="validLocations"
          v-model:value="selectedLocations"
          :auto-close="false"
          placeholder="Select Locations"
          itemsName="Locations"
          text-field="locationString"
          data-item-key="id"
          required
        />
      </div>
      <div class="mb-20">
        <label class="form-label" for="facility-description">Description</label>
        <textarea
          rows="5"
          id="facility-description"
          v-model="description"
          :maxlength="500"
        ></textarea>
      </div>
      <LoadingButton type="submit" theme-color="primary" :loading="false"
        >Submit</LoadingButton
      >
      <br />
    </div>
    <div class="facility-form__contact">
      <div class="facility-form___close" @click="$emit('closeForm')">
        <font-awesome-icon icon="times" />
      </div>
      <div v-if="contacts.length > 0" class="mb-20">
        <label class="form-label" for="contact-dropdown">Contacts</label>
        <DropDownList
          id="contact-dropdown"
          text-field="Contact.Email"
          data-item-key="Contact.Id"
          :data-items="contacts"
          v-model="selectedContact"
          :disabled="contacts.length == 0"
        />
      </div>
      <div>
        <label class="form-label" for="website-url">Website</label>
        <input
          class="form-input w-100"
          type="text"
          id="website-url"
          v-model="websiteUrl"
          :required="false"
        />
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import {
  DropDownList,
  type DropDownListFilterChangeEvent,
  type DropDownListPageChangeEvent,
} from "@progress/kendo-vue-dropdowns";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import type { CoPackerFacilitiesLocation } from "@/Domain/CoPackerFacilitiesLocation";
import type { CoPackerFacilitiesCategory } from "@/Domain/CoPackerFacilitiesCategory";
import type { NameValue } from "@/Domain/NameValue";
import { computed, onBeforeMount, ref, watch } from "vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import MultiSelect from "@/shared/ui/inputs/MultiSelect.vue";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { CoPackerFacilities } from "@/Domain/CoPackerFacilities";
import type { CompanycontactsTb } from "@/Domain/CompanycontactsTb";
import { filterBy } from "@progress/kendo-data-query";
import { Loader } from "@progress/kendo-vue-indicators";

interface CoPackerFacilitiesLocationExtended
  extends CoPackerFacilitiesLocation {
  locationString: string;
}

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  industries: CoPackerFacilitiesCategory[];
  locations: CoPackerFacilitiesLocation[];
  facility?: CoPackerFacilities;
}>();

const emit = defineEmits<{
  (e: "closeForm"): void;
}>();

const companies = ref<NameValue[]>([]);
const filteredCompanies = ref<NameValue[]>([]);
const pagedCompanies = ref<NameValue[]>([]);

const contacts = ref<CompanycontactsTb[]>([]);

const selectedIndustries = ref<CoPackerFacilitiesCategory[]>([]);
const selectedLocations = ref<CoPackerFacilitiesLocation[]>([]);
const selectedCompany = ref<NameValue>();
const selectedContact = ref<CompanycontactsTb>();

const websiteUrl = ref<string>("");
const description = ref<string>("");

const formSubmitting = ref<boolean>(false);

const sortedIndustries = computed(() => {
  return [...props.industries].sort((a, b) =>
    a.category.localeCompare(b.category)
  );
});

const validLocations = computed(() => {
  return [...(props.locations as CoPackerFacilitiesLocationExtended[])]
    .sort((a, b) => {
      // USA should be always on top
      if (a.locationCountry == "USA" && b.locationCity != "USA") return -1;
      if (b.locationCountry == "USA" && a.locationCity != "USA") return 1;
      return a.locationString.localeCompare(b.locationString);
    })
    .filter((l) => l.location);
});

const companyPageSize = ref<number>(12);
const companySkip = ref<number>(0);
const companiesLoaded = ref<boolean>(false);

const companyVirtual = computed(() => {
  return {
    pageSize: companyPageSize.value,
    skip: companySkip.value,
    total: filteredCompanies.value.length,
  };
});

const getAvailableCompaniesAsync = async function () {
  await handleResponsePromiseAsync(
    api.get("GetAvailableCoPackerCompanies", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
    }),
    (data) => {
      companies.value = data.sort((a, b) => a.Name.localeCompare(b.Name));
      filteredCompanies.value = companies.value.slice();
      pagedCompanies.value = companies.value.slice(
        companySkip.value,
        companySkip.value + companyPageSize.value
      );
    },
    "CopackerFacilityForm",
    "getAvailableCompaniesAsync"
  );
  companiesLoaded.value = true;
};

const onCompanyFilterChange = function (e: DropDownListFilterChangeEvent) {
  filteredCompanies.value = filterBy(companies.value.slice(), e.filter);
  pagedCompanies.value = filteredCompanies.value.slice(
    0,
    companyPageSize.value
  );
  companySkip.value = 0;
};

const onCompanyChanged = function () {
  selectedContact.value = {} as CompanycontactsTb;
  //console.debug("selected company: " + selectedCompany.value?.Value)
  getContactsAsync();
};

const onCompanyPageChange = function (e: DropDownListPageChangeEvent) {
  const skip = e.page.skip;
  const take = e.page.take;
  pagedCompanies.value = filteredCompanies.value.slice(skip, skip + take);
  companySkip.value = skip;
};

const getContactsAsync = async function () {
  if (!selectedCompany.value) return;

  const contactsPromise = api.get("GetCompanyContacts", {
    userLogin: main.userLogin,
    companyLogin: selectedCompany.value.Value,
    fields: "",
    operators: "",
    values: "",
  });

  await handleResponsePromiseAsync(
    contactsPromise,
    (data) => {
      contacts.value = data.sort((a, b) =>
        a.Contact.Email.localeCompare(b.Contact.Email)
      );
    },
    "CopackerFacilityForm",
    "getContactsAsync"
  );
};

const onSubmit = async function () {
  if (formSubmitting.value) return;
  try {
    if (!selectedCompany.value) return;

    formSubmitting.value = true;

    const params = {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      coPackerCompanyId: Number(selectedCompany.value!.Value) ?? 0,
      webSiteUrl: websiteUrl.value,
      description: description.value,
      categoryNames: selectedIndustries.value.map((i) => i.category).join(";"),
      locationNames: selectedLocations.value.map((i) => i.location).join(";"),
      contactId: selectedContact.value?.Contact?.Id ?? 0,
    };

    const paramsUpdate = {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      coPackerCompanyId: Number(selectedCompany.value!.Value) ?? 0,
      webSiteUrl: websiteUrl.value,
      description: description.value,
      categoryNames: selectedIndustries.value.map((i) => i.category).join(";"),
      locationNames: selectedLocations.value.map((i) => i.location).join(";"),
      contactId: selectedContact.value?.Contact?.Id ?? 0,
      copackerId: props.facility?.id ?? 0,
    };

    //console.debug("onSubmit() params.copackercompanyid: " + params.coPackerCompanyId)

    if (props.facility) {
      //console.debug("update copacker facility params companyId : " + params.coPackerCompanyId + "/" + Number(selectedCompany.value!.Value) ?? 99)
      await handleResponsePromiseAsync(
        api.get("UpdateCoPackerFacility", paramsUpdate),
        () => {
          notificationStore.notify({
            type: NotificationTypeEnum.success,
            message: "Facility has been updated successfully",
            duration: 5000,
          });
          emit("closeForm");
        },
        "CopackerFacilityForm",
        "onSubmit",
        () => {
          notificationStore.notify({
            type: NotificationTypeEnum.error,
            message: "Error while updating the facility. Please try again",
            duration: 5000,
          });
        }
      );
    } else {
      //console.debug("add copacker facility params companyId : " + params.coPackerCompanyId + "/" + Number(selectedCompany.value!.Value) ?? 99)
      await handleResponsePromiseAsync(
        api.get("AddCoPackerFacility", params),
        () => {
          notificationStore.notify({
            type: NotificationTypeEnum.success,
            message: "New facility has been added successfully",
            duration: 5000,
          });
          emit("closeForm");
        },
        "CopackerFacilityForm",
        "onSubmit",
        () => {
          notificationStore.notify({
            type: NotificationTypeEnum.error,
            message: "Error while adding new facility. Please try again",
            duration: 5000,
          });
        }
      );
    }
  } finally {
    formSubmitting.value = false;
  }
};

const fillFormDataAsync = async function () {
  if (!props.facility) return;

  selectedCompany.value = {
    Name: props.facility.company.Name,
    Value: props.facility.company.CompanyId.toString(),
  };
  websiteUrl.value = props.facility.websiteUrl;
  description.value = props.facility.description;
  // BE return only distinct categories and locations,
  // we don't need to filter them by id
  selectedIndustries.value = props.industries.filter((i) =>
    props.facility!.categories.some((c) => c.category == i.category)
  );

  selectedLocations.value = props.locations.filter((i) =>
    props.facility!.locations.some(
      (l) =>
        l.locationState == i.locationState &&
        l.locationProvince == i.locationProvince &&
        l.locationCity == i.locationCity &&
        l.location == i.location
    )
  );

  await getContactsAsync();

  selectedContact.value = contacts.value.find((c) => c.copackerCT);
};

const updateLocationValues = function () {
  (props.locations as CoPackerFacilitiesLocationExtended[]).forEach((l) => {
    l.locationString = `${l.locationCountry}, ${l.location}`;
  });
};

watch(
  () => props.facility,
  () => {
    fillFormDataAsync();
  }
);

watch(
  () => props.locations,
  () => {
    updateLocationValues();
  }
);

onBeforeMount(() => {
  getAvailableCompaniesAsync();
  fillFormDataAsync();
  updateLocationValues();
});
</script>

<style lang="scss">
.facility-form {
  &__header {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 10px;
  }

  &__main {
    flex: 3;
    padding: 20px;
  }

  &__contact {
    flex: 1;
    background: #f7f7f7;
    border-left: 1px solid #efefef;
    padding: 20px;
    min-width: 300px;
    max-width: 300px;
    word-break: break-all;
  }

  &___close {
    font-size: 18px;
    margin-bottom: 10px;
    text-align: end;
    cursor: pointer;
    &:hover {
      color: #444;
    }
  }

  .form-control {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    gap: 12px;

    @media (max-width: 1024px) {
      flex-wrap: wrap;
    }
  }

  .form-label {
    display: block;
    margin-bottom: 5px;
    font-weight: bold;
    font-size: 15px;
    color: #666;
    width: 25%;
  }

  .company-loader {
    width: 100%;
    .k-loader-canvas {
      margin: 0 auto;
    }
  }

  .mb-20 {
    margin-bottom: 20px;
  }

  .w-100 {
    width: 100%;
  }
}
</style>
